import { useState } from 'react';
export type Bar = {
  key: string;
  label: string;
  values: number[];
};
type Series = {
  label: string;
  className: string;
};
type Props = {
  bars: Bar[];
  /** Eén of meer gestapelde reeksen; de legenda verschijnt vanaf twee. */
  series: Series[];
  format: (n: number) => string;
  /** Vaste bovengrens (bijv. 100 voor procenten). */
  max?: number;
  empty?: string;
};

/**
 * Eenvoudige staafjes, eventueel gestapeld. De waarde verschijnt bij aanwijzen én bij tikken
 * (een telefoon heeft geen hover). Hoort in een ChartFrame: daar staan dezelfde cijfers als tabel.
 */
export function BarChart({
  bars,
  series,
  format,
  max,
  empty = 'Niets in deze periode.'
}: Props) {
  const [active, setActive] = useState<string | null>(null);
  const top = max ?? Math.max(1, ...bars.map(b => b.values.reduce((s, v) => s + v, 0)));
  const nothing = bars.every(b => b.values.every(v => v <= 0));
  const every = Math.max(1, Math.ceil(bars.length / 12));
  const last = bars.length - 1;
  return <div data-id="element-1752" data-mp="a3tgo0godtqg">
      {series.length > 1 && <ul className="mb-3 flex flex-wrap gap-4 text-[12px] text-muted" data-id="element-1753" data-mp="a436no1q00w5">
          {series.map(s => <li key={s.label} className="inline-flex items-center gap-1.5" data-id="element-1754" data-mp="f1v7x8o98zd0">
              <span className={`h-2.5 w-2.5 rounded-sm ${s.className}`} data-id="element-1755" data-mp="123nap38gkq1" />
              {s.label}
            </li>)}
        </ul>}
      <div className="relative" data-id="element-1756" data-mp="56bfiw8wgit0">
        {nothing && <p className="absolute inset-x-0 top-1/3 text-center text-[13px] text-muted" data-id="element-1757" data-mp="3nhexnxppvznb">{empty}</p>}
        <div className="flex h-44 items-end gap-[3px] sm:h-52" onPointerLeave={() => setActive(null)} data-id="element-1758" data-mp="3sa82ell6r7ze">
          {bars.map((b, i) => {
          const total = b.values.reduce((s, v) => s + v, 0);
          const shown = active === b.key;
          const place = i < 2 ? 'left-0' : i > last - 2 ? 'right-0' : 'left-1/2 -translate-x-1/2';
          return <div key={b.key} className="relative flex h-full min-w-0 flex-1 cursor-default flex-col-reverse" onPointerEnter={e => e.pointerType === 'mouse' && setActive(b.key)} onClick={() => setActive(a => a === b.key ? null : b.key)} data-id="element-1759" data-mp="39ioxuabijlv9" data-mpi={i}>
                {total <= 0 ? <div className="h-[2px] w-full bg-line" data-id="element-1760" data-mp="15d6u226a7u71" /> : b.values.map((v, si) => <div key={si} className={`w-full transition-opacity duration-150 ease-soft first:rounded-b-none last:rounded-t-sm ${series[si]?.className ?? 'bg-clay'} ${active && !shown ? 'opacity-60' : ''}`} style={{
              height: `${Math.max(0, v) / top * 100}%`
            }} data-id="element-1761" data-mp="1198rfhhr0198" data-mpi={si} />)}
                {shown && <span className={`pointer-events-none absolute bottom-full z-raised mb-1 whitespace-nowrap rounded bg-ink px-2 py-1 text-[11px] text-shell ${place}`} data-id="element-1762" data-mp="dm0lqygeqq6o">
                    {b.label} · {series.length > 1 ? series.map((s, si) => `${s.label} ${format(b.values[si] ?? 0)}`).join(' · ') : format(total)}
                  </span>}
              </div>;
        })}
        </div>
      </div>
      <div className="mt-1.5 flex gap-[3px] text-[11px] tabular-nums text-muted" data-id="element-1763" data-mp="2t9d68gppwjuz">
        {bars.map((b, i) => <span key={b.key} className="min-w-0 flex-1 truncate text-center" data-id="element-1764" data-mp="2y2pjxvsuz68y" data-mpi={i}>
            {i % every === 0 ? b.label : ''}
          </span>)}
      </div>
    </div>;
}